/**
 * Client-side export honoring the *current* filter/sort/visible columns
 * (ListView §3.2 ExportMenu contract). Pure, unit-testable formatting
 * functions plus thin browser-only download triggers.
 *
 * Known gap (flagged in the UX report): the IB-4 API contract has no
 * server-side export job endpoint, so this exports the rows already loaded
 * into the grid (the current page/pages), not every row matching the filter
 * across the whole tenant. A later story should add a backend export
 * endpoint for large result sets; this is a real, working action in the
 * meantime — not a placeholder.
 */

export interface ExportColumn<T> {
  id: string
  header: string
  toText: (row: T) => string
}

export function toCsv<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const escape = (value: string): string => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }
  const header = columns.map((c) => escape(c.header)).join(',')
  const body = rows.map((row) => columns.map((c) => escape(c.toText(row))).join(','))
  return [header, ...body].join('\r\n')
}

/** A minimal HTML table Excel opens natively — no OOXML zip library in this slice. */
export function toExcelHtml<T>(rows: readonly T[], columns: readonly ExportColumn<T>[]): string {
  const escape = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const head = `<tr>${columns.map((c) => `<th>${escape(c.header)}</th>`).join('')}</tr>`
  const body = rows
    .map((row) => `<tr>${columns.map((c) => `<td>${escape(c.toText(row))}</td>`).join('')}</tr>`)
    .join('')
  return `<html><head><meta charset="UTF-8"></head><body><table>${head}${body}</table></body></html>`
}

export function toPrintableHtml<T>(
  title: string,
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): string {
  const escape = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const head = `<tr>${columns.map((c) => `<th>${escape(c.header)}</th>`).join('')}</tr>`
  const body = rows
    .map((row) => `<tr>${columns.map((c) => `<td>${escape(c.toText(row))}</td>`).join('')}</tr>`)
    .join('')
  // This markup renders in a separate print window/tab that never loads
  // tokens.css, so CSS custom properties are unavailable — the values below
  // are hand-copied from the graphite neutral scale (--iw-text,
  // --iw-border, --iw-neutral-100) and must be kept in sync with
  // tokens.css by hand if that scale ever changes. Not a component;
  // deliberately the one place in the frontend a literal hex is correct.
  return `<html><head><meta charset="UTF-8"><title>${escape(title)}</title>
<style>
  body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1f2533}
  table{width:100%;border-collapse:collapse}
  th,td{border:1px solid #d3d9e4;padding:4px 8px;text-align:left}
  th{background:#f0f2f6}
</style></head><body><h1>${escape(title)}</h1><table>${head}${body}</table></body></html>`
}

function downloadBlob(content: string, mimeType: string, filename: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export type ExportFormat = 'csv' | 'xlsx' | 'pdf'

/**
 * Reporting & Exports honesty rule (CLAUDE.md "keep exports honest") — every list export button
 * shows this note whenever the grid has not loaded every row matching the current view (i.e. the
 * cursor-paginated list still has a `nextCursor`). `undefined` means nothing to disclose (every
 * matching row is already loaded), so callers can hand it straight to `ListView.exportNote`.
 */
export function exportCapNote(loadedCount: number, hasMore: boolean): string | undefined {
  if (!hasMore) return undefined
  return `Exports only the ${loadedCount} row${loadedCount === 1 ? '' : 's'} loaded so far — more rows match this view. Use "Load more" to include them before exporting.`
}

export function runExport<T>(
  format: ExportFormat,
  title: string,
  rows: readonly T[],
  columns: readonly ExportColumn<T>[],
): void {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
  if (format === 'csv') {
    downloadBlob(toCsv(rows, columns), 'text/csv;charset=utf-8', `${slug}.csv`)
    return
  }
  if (format === 'xlsx') {
    downloadBlob(toExcelHtml(rows, columns), 'application/vnd.ms-excel', `${slug}.xls`)
    return
  }
  // PDF: no client-side PDF library in this slice and the branded
  // pinned-Chromium pipeline (ADR-0001) is a backend/Playwright concern, not
  // reachable from the browser bundle — open a print-formatted view and let
  // the browser's native "Save as PDF" do the rendering. Real, defined
  // action; not a dead button.
  const printWindow = window.open('', '_blank', 'noopener,noreferrer')
  if (!printWindow) return
  printWindow.document.write(toPrintableHtml(title, rows, columns))
  printWindow.document.close()
  printWindow.focus()
  printWindow.print()
}
